import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import loginimg from "../../Assets/Login/Adminlogin.jpg";

const AdminLogin = () => {
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [adminData, setAdminData] = useState(null);
  const otpInputs = useRef([]);
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  const CORRECT_OTP = "111111";

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
    });
  }, []);

  // Step 1: Verify Phone from Backend
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (phoneNumber.length !== 10 || !/^\d+$/.test(phoneNumber)) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/admins/admin-login-by-phone`, {
        phoneNumber,
      });

      if (data.success) {
        toast.success("OTP sent successfully! (Demo OTP: 111111)");
        setAdminData(data.data);
        setStep("otp");
        setTimeout(() => otpInputs.current[0]?.focus(), 300);

        localStorage.setItem("login-data", JSON.stringify(data.data));

      } else {
        toast.error(data.error || "Admin not found");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Something went wrong");
    }
  };

  // Step 2: OTP Logic
  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpInputs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  // Step 3: Validate OTP
  const handleOtpSubmit = (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) return setError("Please enter complete OTP");

    if (enteredOtp === CORRECT_OTP) {
      toast.success("Login successful!");
      
      // Use AuthContext for login
      adminLogin(adminData);
      
      setTimeout(() => navigate("/dashboard"), 1500);
    } else {
      setError("Invalid OTP. Please try again. (Hint: 111111)");
      toast.error("Invalid OTP!");
      setOtp(["", "", "", "", "", ""]);
      otpInputs.current[0]?.focus();
    }
  };

  const handleBackToPhone = () => {
    setStep("phone");
    setOtp(["", "", "", "", "", ""]);
    setError("");
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full shadow-2xl"
          data-aos="zoom-in"
        >
          <div className="grid md:grid-cols-2">
            {/* Left Side - Image */}
            <div
              className="relative bg-cover flex items-center justify-center"
              data-aos="fade-right"
            >
              <img
                src={loginimg}
                alt="Admin Login Illustration"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Right Side - Form */}
            <div
              className="p-8 md:p-12 relative flex flex-col justify-center bg-white"
              data-aos="fade-left"
            >
              <div className="mb-8 text-center">
                <h2 className="text-lg md:text-2xl font-philosopher text-gray-900 mb-2">
                  Admin Access Portal
                </h2>
                <h1 className="text-xl md:text-3xl font-philosopher text-gray-900 mb-4 leading-snug">
                  Secure Login <br /> for E-Commerce Dashboard
                </h1>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed text-justify">
                  Manage orders, track analytics, and control your store settings
                  — all from one place. Verify your number to access your admin panel.
                </p>
              </div>

              {/* Step 1: Phone Input */}
              {step === "phone" && (
                <div className="transition-all duration-700" data-aos="fade-up">
                  <form onSubmit={handlePhoneSubmit}>
                    <input
                      type="tel"
                      placeholder="Enter Admin Phone Number"
                      value={phoneNumber}
                      onChange={(e) =>
                        setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none mb-4"
                      maxLength="10"
                    />
                    {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                    <button
                      type="submit"
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium"
                    >
                      Send OTP
                    </button>
                  </form>
                </div>
              )}

              {/* Step 2: OTP Input */}
              {step === "otp" && (
                <div className="transition-all duration-700" data-aos="fade-up">
                  <button
                    onClick={handleBackToPhone}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm">Change Number</span>
                  </button>

                  <p className="text-sm text-gray-600 mb-6">
                    OTP sent to <span className="font-semibold">{phoneNumber}</span>
                  </p>

                  <form onSubmit={handleOtpSubmit}>
                    <div className="flex gap-2 justify-between mb-6">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpInputs.current[index] = el)}
                          type="text"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-10 h-10 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      ))}
                    </div>

                    {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

                    <button
                      type="submit"
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium mb-4"
                    >
                      Verify & Login
                    </button>

                    <p className="text-center text-sm text-gray-600">
                      Didn't receive OTP?{" "}
                      <button
                        type="button"
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        Resend
                      </button>
                    </p>

                    <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-blue-800">
                        💡 Demo OTP: <span className="font-bold">111111</span>
                      </p>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
